import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { jsPDF } from 'jspdf';
import { formatDate } from './config';

const formatCurrency = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

const UNICODE_FONT_NAME = 'Devanagari';
const UNICODE_FONT_FILE_NAME = 'Devanagari.ttf';
const UNICODE_FONT_URLS = ['/fonts/NotoSansDevanagari-Regular.ttf', '/fonts/Mangal.ttf'];

const sanitizeFileName = (value) =>
    String(value || '')
        .replace(/[^a-z0-9-_]+/gi, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();

let unicodeFontBase64Cache = null;
let unicodeFontLoadPromise = null;

const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    const chunks = [];
    for (let i = 0; i < bytes.length; i += chunkSize) {
        chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
    }
    return btoa(chunks.join(''));
};

const isProbablyFontFile = (buffer) => {
    if (!buffer || buffer.byteLength < 4) return false;
    const bytes = new Uint8Array(buffer.slice(0, 4));
    const asText = String.fromCharCode(...bytes);
    if (asText === 'OTTO') return true;
    if (asText === 'ttcf') return true;
    return bytes[0] === 0x00 && bytes[1] === 0x01 && bytes[2] === 0x00 && bytes[3] === 0x00;
};

const hasDevanagariText = (value) => /[\u0900-\u097F]/.test(String(value || ''));

const loadUnicodeFontBase64 = async () => {
    if (unicodeFontBase64Cache) return unicodeFontBase64Cache;
    if (unicodeFontLoadPromise) return unicodeFontLoadPromise;
    unicodeFontLoadPromise = (async () => {
        for (const url of UNICODE_FONT_URLS) {
            try {
                const res = await fetch(url, { cache: 'force-cache' });
                if (!res.ok) continue;
                const buffer = await res.arrayBuffer();
                if (!isProbablyFontFile(buffer)) continue;
                const base64 = arrayBufferToBase64(buffer);
                unicodeFontBase64Cache = base64;
                return base64;
            } catch (e) {
                continue;
            }
        }
        return null;
    })();
    return unicodeFontLoadPromise;
};

const ensureUnicodeFont = async (doc) => {
    try {
        const list = typeof doc.getFontList === 'function' ? doc.getFontList() : null;
        if (list && list[UNICODE_FONT_NAME]) {
            doc.setFont(UNICODE_FONT_NAME, 'normal');
            return true;
        }
        const base64 = await loadUnicodeFontBase64();
        if (!base64) return false;
        try {
            doc.addFileToVFS(UNICODE_FONT_FILE_NAME, base64);
            doc.addFont(UNICODE_FONT_FILE_NAME, UNICODE_FONT_NAME, 'normal');
            doc.setFont(UNICODE_FONT_NAME, 'normal');
        } catch (e) {
            unicodeFontBase64Cache = null;
            unicodeFontLoadPromise = null;
            return false;
        }
        return true;
    } catch (e) {
        return false;
    }
};

const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = String(reader.result || '');
            const [, base64 = ''] = result.split(',');
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

const downloadBlob = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
};

const drawRow = (doc, cells, columnWidths, startX, startY, rowHeight, options = {}) => {
    const alignments = Array.isArray(options.alignments) ? options.alignments : [];
    const topPadding = Number(options.topPadding) || 4;
    const cellPaddingX = Number(options.cellPaddingX) || 3;
    let cursorX = startX;
    cells.forEach((cell, index) => {
        const cellWidth = columnWidths[index];
        doc.rect(cursorX, startY, cellWidth, rowHeight);
        const text = Array.isArray(cell) ? cell : [String(cell ?? '')];
        const align = alignments[index] || 'left';
        const textX = align === 'right' ? cursorX + cellWidth - cellPaddingX : cursorX + cellPaddingX;
        doc.text(text, textX, startY + topPadding, { baseline: 'top', align });
        cursorX += cellWidth;
    });
};

const fitText = (doc, value, width) => doc.splitTextToSize(String(value ?? '-'), Math.max(12, width - 4));

const formatPaymentSplit = (cash, upi, bank) => {
    const c = Number(cash) || 0;
    const u = Number(upi) || 0;
    const b = Number(bank) || 0;
    const parts = [];
    if (c > 0) parts.push(`Cash ${formatCurrency(c)}`);
    if (u > 0) parts.push(`UPI ${formatCurrency(u)}`);
    if (b > 0) parts.push(`Bank ${formatCurrency(b)}`);
    return parts.length > 0 ? parts.join(' | ') : '-';
};

const getReportFileName = ({ customerName, fromDate, toDate }) => {
    const customerSlug = sanitizeFileName(customerName || 'customer');
    return `sale-report-${customerSlug || 'customer'}-${fromDate || 'from'}-to-${toDate || 'to'}.pdf`;
};

const buildSaleInvoicePdf = async ({ invoice, customer, vehicle, previousBalance, firm }) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a5' });
    const unicodeReady = await ensureUnicodeFont(doc);
    const headerText = [
        firm?.name || '',
        customer?.name || invoice?.customer_name || '',
        vehicle?.vehicle_no || invoice?.vehicle_no || ''
    ].join(' ');
    if (!unicodeReady && hasDevanagariText(headerText)) {
        throw new Error(
            'Unicode font missing for Marathi. Upload NotoSansDevanagari-Regular.ttf to /public/fonts and redeploy.'
        );
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    const columnWidths = [20, 185, 44, 52, 70];
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const startX = margin;
    let cursorY = margin;

    const totalAmount = Number(invoice?.total_amount || 0);
    const paidAmount = Number(invoice?.paid_amount || 0);
    const prevAmount = Number(previousBalance || 0);
    const remainingAmount = prevAmount + totalAmount - paidAmount;
    const items = Array.isArray(invoice?.items) ? invoice.items : [];
    const paymentLines = Array.isArray(invoice?.payments) ? invoice.payments : [];
    const contentWidth = pageWidth - margin * 2;
    const halfWidth = contentWidth / 2;
    const boxHeight = 62;
    const customerName = customer?.name || invoice?.customer_name || '-';
    const customerMobile = invoice?.service_customer_mobile || customer?.contact || invoice?.customer_contact || '-';
    const vehicleNo = invoice?.service_vehicle_no || vehicle?.vehicle_no || invoice?.vehicle_no || '-';
    const vehicleModel = invoice?.service_vehicle_model || vehicle?.vehicle_model || '-';
    const kmReading = invoice?.service_km || '-';
    const paymentSummary = paymentLines.length > 0
        ? paymentLines.map((line) => `${line.payment_mode}: ${formatCurrency(line.amount)}`).join(' | ')
        : '-';

    const drawInfoLine = (label, value, x, y, width) => {
        doc.setFontSize(5.8);
        doc.setFont(undefined, 'bold');
        doc.text(`${label}:`, x + 3, y);
        doc.setFont(undefined, 'normal');
        const text = doc.splitTextToSize(String(value || '-'), Math.max(20, width - 34));
        doc.text(text, x + 28, y);
    };

    const drawTableHeader = () => {
        doc.setFillColor(235, 235, 235);
        doc.rect(startX, cursorY, totalWidth, 15, 'F');
        drawRow(doc, ['S.No', 'Description', 'Qty', 'Rate', 'Amount'], columnWidths, startX, cursorY, 15, {
            alignments: ['center', 'left', 'center', 'right', 'right'],
            topPadding: 4,
            cellPaddingX: 3
        });
        cursorY += 15;
    };

    doc.setLineWidth(0.8);
    doc.rect(margin, margin, contentWidth, pageHeight - margin * 2);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(String(firm?.name || 'Service Invoice'), pageWidth / 2, cursorY + 10, { align: 'center' });
    cursorY += 12;

    doc.setFontSize(5.8);
    doc.setFont(undefined, 'normal');
    const firmAddress = [firm?.address, firm?.contact ? `Contact: ${firm.contact}` : '', firm?.email ? `Email: ${firm.email}` : '']
        .filter(Boolean)
        .join(' | ');
    if (firmAddress) {
        const lines = doc.splitTextToSize(firmAddress, contentWidth - 14);
        doc.text(lines, pageWidth / 2, cursorY + 7, { align: 'center' });
        cursorY += lines.length * 7;
    }
    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.text('Service Invoice', pageWidth / 2, cursorY + 8, { align: 'center' });
    cursorY += 12;

    doc.rect(startX, cursorY, halfWidth, boxHeight);
    doc.rect(startX + halfWidth, cursorY, halfWidth, boxHeight);

    drawInfoLine('Vehicle No', vehicleNo, startX, cursorY + 10, halfWidth);
    drawInfoLine('Customer', customerName, startX, cursorY + 21, halfWidth);
    drawInfoLine('Mobile', customerMobile, startX, cursorY + 32, halfWidth);
    drawInfoLine('Ch. No', invoice?.chassis_no || '-', startX, cursorY + 43, halfWidth);

    drawInfoLine('Date', formatDate(invoice?.date), startX + halfWidth, cursorY + 10, halfWidth);
    drawInfoLine('Model', vehicleModel, startX + halfWidth, cursorY + 21, halfWidth);
    drawInfoLine('Km', kmReading, startX + halfWidth, cursorY + 32, halfWidth);
    if (invoice?.job_card_no) {
        drawInfoLine('Job Card', invoice.job_card_no, startX + halfWidth, cursorY + 43, halfWidth);
    }
    cursorY += boxHeight;

    drawTableHeader();
    doc.setFontSize(6);

    items.forEach((item, index) => {
        const productLines = doc.splitTextToSize(String(item?.product_name || '-'), columnWidths[1] - 6);
        const rowHeight = Math.max(12, productLines.length * 7 + 3);

        if (cursorY + rowHeight > pageHeight - 86) {
            doc.addPage();
            doc.rect(margin, margin, contentWidth, pageHeight - margin * 2);
            cursorY = margin + 10;
            drawTableHeader();
            doc.setFontSize(6);
        }

        drawRow(
            doc,
            [
                String(index + 1),
                productLines,
                `${Number(item?.quantity || 0)}`,
                Number(item?.price || 0).toFixed(2),
                Number(item?.total || 0).toFixed(2)
            ],
            columnWidths,
            startX,
            cursorY,
            rowHeight,
            {
                alignments: ['center', 'left', 'center', 'right', 'right'],
                topPadding: 3,
                cellPaddingX: 3
            }
        );
        cursorY += rowHeight;
    });

    const footerY = Math.min(pageHeight - 58, Math.max(cursorY + 6, pageHeight - 74));
    doc.rect(startX, footerY, totalWidth, 40);
    doc.line(startX + 72, footerY, startX + 72, footerY + 40);
    doc.line(startX + 140, footerY, startX + 140, footerY + 40);
    doc.line(startX + totalWidth - 92, footerY, startX + totalWidth - 92, footerY + 40);

    doc.setFontSize(5.2);
    doc.text('Customer Sign', startX + 14, footerY + 34);
    doc.text('Receiver', startX + 92, footerY + 34);

    doc.setFontSize(6.4);
    doc.setFont(undefined, 'bold');
    doc.text('Total Amount', startX + totalWidth - 84, footerY + 12);
    doc.setFontSize(8.5);
    doc.text(Number(totalAmount || 0).toFixed(2), startX + totalWidth - 10, footerY + 24, { align: 'right' });

    doc.setFontSize(5.4);
    doc.setFont(undefined, 'normal');
    doc.text(`Paid: ${formatCurrency(paidAmount)}`, startX + 144, footerY + 12);
    doc.text(`Previous: ${formatCurrency(prevAmount)}`, startX + 144, footerY + 21);
    doc.text(`Balance: ${formatCurrency(remainingAmount)}`, startX + 144, footerY + 30);
    if (paymentSummary !== '-') {
        const splitLines = doc.splitTextToSize(`Payment: ${paymentSummary}`, totalWidth - 236);
        doc.text(splitLines, startX + 144, footerY + 38);
    }

    return doc;
};

const tryNativeShare = async ({ blob, fileName, title, text }) => {
    if (!Capacitor.isNativePlatform()) return false;
    const base64 = await blobToBase64(blob);
    const path = `shared-invoices/${fileName}`;
    const writeResult = await Filesystem.writeFile({
        path,
        data: base64,
        directory: Directory.Cache,
        recursive: true
    });

    await Share.share({
        title,
        text,
        url: writeResult.uri,
        dialogTitle: 'Share Service Invoice PDF'
    });
    return true;
};

const tryBrowserShare = async ({ blob, fileName, title, text }) => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const file = new File([blob], fileName, { type: 'application/pdf' });
    if (typeof navigator.canShare !== 'function' || !navigator.canShare({ files: [file] })) {
        return false;
    }
    await navigator.share({
        title,
        text,
        files: [file]
    });
    return true;
};

export const shareSaleInvoicePdf = async ({ invoice, customer, vehicle, previousBalance, firm }) => {
    const invoiceId = invoice?.id || invoice?.invoice_id || 'invoice';
    const customerSlug = sanitizeFileName(customer?.name || invoice?.customer_name || 'customer');
    const fileName = `sale-invoice-${invoiceId}-${customerSlug || 'customer'}.pdf`;
    const title = `Service Invoice #${invoiceId}`;
    const text = `Sharing Service Invoice #${invoiceId}`;
    const pdf = await buildSaleInvoicePdf({ invoice, customer, vehicle, previousBalance, firm });
    const blob = pdf.output('blob');

    try {
        const shared = await tryNativeShare({ blob, fileName, title, text });
        if (shared) return { shared: true, mode: 'native' };
    } catch (error) {
        console.error('Native PDF share failed', error);
    }

    try {
        const shared = await tryBrowserShare({ blob, fileName, title, text });
        if (shared) return { shared: true, mode: 'browser' };
    } catch (error) {
        console.error('Browser PDF share failed', error);
    }

    downloadBlob(blob, fileName);
    return { shared: false, mode: 'download' };
};

const buildSalesReportPdf = async ({ rows, title, customerName, fromDate, toDate }) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const unicodeReady = await ensureUnicodeFont(doc);
    if (!unicodeReady && hasDevanagariText(customerName)) {
        throw new Error(
            'Unicode font missing for Marathi. Upload NotoSansDevanagari-Regular.ttf to /public/fonts and redeploy.'
        );
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const startX = margin;
    const columnWidths = [58, 76, 82, 70, 150, 40, 72, 58, 92, 58, 92, 60];
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const headers = [
        'Date',
        'Vehicle',
        'Model',
        'Job Card',
        'Items',
        'Qty',
        'Invoice Total',
        'Paid Amount',
        'Paid Split',
        'Receipt',
        'Receipt Split',
        'Net'
    ];
    let cursorY = margin + 8;

    const drawHeader = () => {
        doc.setFontSize(13);
        doc.text(title || 'Service Sales Report', startX, cursorY);
        cursorY += 14;

        doc.setFontSize(8);
        doc.text(`Customer: ${customerName || '-'}`, startX, cursorY);
        doc.text(`From: ${formatDate(fromDate)}`, pageWidth / 2 - 45, cursorY);
        doc.text(`To: ${formatDate(toDate)}`, pageWidth - margin - 75, cursorY);
        cursorY += 12;

        doc.setFillColor(235, 235, 235);
        doc.rect(startX, cursorY, totalWidth, 18, 'F');
        let headX = startX;
        headers.forEach((header, index) => {
            doc.rect(headX, cursorY, columnWidths[index], 18);
            doc.text(fitText(doc, header, columnWidths[index]), headX + 2, cursorY + 11);
            headX += columnWidths[index];
        });
        cursorY += 18;
    };

    drawHeader();
    doc.setFontSize(6.5);

    rows.forEach((row) => {
        const cells = [
            formatDate(row.date),
            String(row.vehicle_no || '-'),
            String(row.service_vehicle_model || '-'),
            String(row.job_card_no || '-'),
            String(row.items_summary || '-'),
            String(Number(row.total_qty || 0)),
            formatCurrency(row.total_amount),
            formatCurrency(row.paid_amount),
            formatPaymentSplit(row.cash_amount, row.upi_amount, row.bank_amount),
            formatCurrency(row.receipt_amount),
            formatPaymentSplit(row.receipt_cash_amount, row.receipt_upi_amount, row.receipt_bank_amount),
            formatCurrency(row.cumulative_balance ?? row.net_balance)
        ];
        const linesByCell = cells.map((cell, index) => fitText(doc, cell, columnWidths[index]));
        const maxLines = linesByCell.reduce((max, lines) => Math.max(max, lines.length), 1);
        const rowHeight = Math.max(16, maxLines * 8 + 4);

        if (cursorY + rowHeight > pageHeight - margin) {
            doc.addPage();
            cursorY = margin + 8;
            drawHeader();
            doc.setFontSize(6.5);
        }

        let cellX = startX;
        linesByCell.forEach((lines, index) => {
            doc.rect(cellX, cursorY, columnWidths[index], rowHeight);
            doc.text(lines, cellX + 2, cursorY + 9);
            cellX += columnWidths[index];
        });
        cursorY += rowHeight;
    });

    if (!rows.length) {
        doc.text('No service sales found for this period.', startX, cursorY + 12);
    }

    return doc;
};

export const shareSalesReportPdf = async ({ rows, customerName, fromDate, toDate }) => {
    const fileName = getReportFileName({ customerName, fromDate, toDate });
    const title = 'Service Sales Report';
    const pdf = await buildSalesReportPdf({
        rows: Array.isArray(rows) ? rows : [],
        title: 'Service Sales Report',
        customerName,
        fromDate,
        toDate
    });
    const blob = pdf.output('blob');

    try {
        const shared = await tryNativeShare({
            blob,
            fileName,
            title,
            text: `Service Sales Report ${formatDate(fromDate)} to ${formatDate(toDate)}`
        });
        if (shared) return { shared: true, mode: 'native' };
    } catch (error) {
        console.error('Native Service Sales Report PDF share failed', error);
    }

    try {
        const shared = await tryBrowserShare({
            blob,
            fileName,
            title,
            text: `Service Sales Report ${formatDate(fromDate)} to ${formatDate(toDate)}`
        });
        if (shared) return { shared: true, mode: 'browser' };
    } catch (error) {
        console.error('Browser Service Sales Report PDF share failed', error);
    }

    downloadBlob(blob, fileName);
    return { shared: false, mode: 'download' };
};
